import { useState, useEffect } from 'react';
import { Destination } from '@/types/iterpec';
import { supabase } from '@/integrations/supabase/client';

export const useDestinations = (searchTerm: string) => {
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchTerm.length < 3) {
      setDestinations([]);
      return;
    }

    const loadDestinations = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('iterpec-autocomplete', {
          body: { searchTerm }
        });

        if (error) throw error;
        
        const mapped: Destination[] = (Array.isArray(data) ? data : []).map((item: any) => {
          const name = item?.name?.es || item?.name?.other || item?.name?.en || item?.hotelName || '';
          const id = String(item?.destinationId ?? item?.id ?? '');
          const type = item?.type || '';
          return { DestinationId: id, Name: name, Type: type } as Destination;
        }).filter((d) => d.DestinationId && d.Name);

        // Remover duplicados
        const seen = new Set<string>();
        const unique = mapped.filter((d) => {
          const key = `${d.DestinationId}-${d.Name}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        // Priorizar: por tipo (Cidade > Região > Bairro > POI > Aeroporto > Hotel),
        // e dentro do mesmo tipo por match exato > prefixo > substring.
        const q = searchTerm.trim().toLowerCase();
        const norm = (s: string) =>
          s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const nq = norm(q);
        const matchScore = (name: string) => {
          const n = norm(name);
          if (n === nq) return 0;
          if (n.startsWith(nq)) return 1;
          const words = n.split(/[\s,]+/);
          if (words.some((w) => w === nq)) return 2;
          if (words.some((w) => w.startsWith(nq))) return 3;
          if (n.includes(nq)) return 4;
          return 5;
        };
        const typePriority: { [key: string]: number } = {
          'City': 1,
          'Multi-City (Vicinity)': 2,
          'Neighborhood': 3,
          'Point of Interest Shadow': 4,
          'Airport': 5,
          'Hotel': 6,
        };
        const sorted = unique.sort((a, b) => {
          const ta = typePriority[a.Type || ''] || 99;
          const tb = typePriority[b.Type || ''] || 99;
          if (ta !== tb) return ta - tb;
          const ma = matchScore(a.Name);
          const mb = matchScore(b.Name);
          if (ma !== mb) return ma - mb;
          return a.Name.length - b.Name.length;
        });

        setDestinations(sorted.slice(0, 20));
      } catch (error) {
        console.error('Error loading destinations:', error);
        setDestinations([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      loadDestinations();
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  return { destinations, loading };
};
